import { useAuthStore } from '~/stores/auth-store'

// Admin REST 클라이언트. 인증은 HTTP-only 쿠키(`admin_token`) 기반이라 모든 요청에
// credentials:"include"를 실어 보낸다(Authorization 헤더 미사용). 응답 에러는
// { code, message } 형태를 가정해 ApiError로 던지고, 401이면 세션을 비운다.

const runtimeBaseUrl = window.__RUNTIME_CONFIG__?.apiBaseUrl ?? '/admin/api'

// 쿼리스트링 빌더 — undefined·빈 문자열 값을 제외하고 ?key=value… 로 직렬화한다.
// 여러 api 모듈이 동일하게 쓰던 toQuery를 하나로 통합한 공용 함수.
export function buildQuery(
    params: Record<string, string | number | undefined>
): string {
    const search = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== '') {
            search.set(key, String(value))
        }
    }
    const qs = search.toString()
    return qs ? `?${qs}` : ''
}

export class ApiError extends Error {
    code: string
    status: number

    constructor(code: string, message: string, status: number) {
        super(message)
        this.code = code
        this.status = status
    }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers)
    headers.set('Content-Type', 'application/json')

    const resp = await fetch(`${runtimeBaseUrl}${path}`, {
        ...init,
        headers,
        credentials: 'include',
    })

    if (resp.status === 401) {
        useAuthStore.getState().clear()
    }

    if (!resp.ok) {
        let code = 'ApiError'
        let message = `요청이 실패했어요 (${resp.status}).`
        try {
            const body = (await resp.json()) as {
                code?: string
                message?: string
            }
            code = body.code ?? code
            message = body.message ?? message
        } catch {
            // JSON 파싱 실패 시 기본 메시지를 유지한다.
        }
        throw new ApiError(code, message, resp.status)
    }

    if (resp.status === 204) {
        return undefined as T
    }
    return (await resp.json()) as T
}

// multipart/form-data 전송. Content-Type은 브라우저가 boundary와 함께 자동 설정하도록
// 비워 두고, Authorization 헤더·401 처리·에러 파싱은 JSON 경로와 동일하게 한다.
async function requestMultipart<T>(path: string, form: FormData): Promise<T> {
    const resp = await fetch(`${runtimeBaseUrl}${path}`, {
        method: 'POST',
        body: form,
        credentials: 'include',
    })

    if (resp.status === 401) {
        useAuthStore.getState().clear()
    }

    if (!resp.ok) {
        let code = 'ApiError'
        let message = `요청이 실패했어요 (${resp.status}).`
        try {
            const body = (await resp.json()) as {
                code?: string
                message?: string
            }
            code = body.code ?? code
            message = body.message ?? message
        } catch {
            // JSON 파싱 실패 시 기본 메시지를 유지한다.
        }
        throw new ApiError(code, message, resp.status)
    }

    if (resp.status === 204) {
        return undefined as T
    }
    return (await resp.json()) as T
}

// JWT 인증이 필요한 파일 다운로드. window.open은 Authorization 헤더를 못 실으므로
// blob으로 받아 임시 <a download>로 저장한다. 파일명은 Content-Disposition에서 추출하되
// 없으면 fallback을 쓴다. 401은 JSON 경로와 동일하게 세션을 비운다.
// body가 있으면 POST(application/json)로, 없으면 GET으로 받는다(선택 내보내기 등 본문 필요한 경우).
async function downloadBlob(
    path: string,
    fallbackName: string,
    body?: unknown
): Promise<void> {
    const headers = new Headers()
    const init: RequestInit =
        body !== undefined
            ? { method: 'POST', body: JSON.stringify(body) }
            : { method: 'GET' }
    if (body !== undefined) headers.set('Content-Type', 'application/json')

    const resp = await fetch(`${runtimeBaseUrl}${path}`, {
        ...init,
        headers,
        credentials: 'include',
    })

    if (resp.status === 401) {
        useAuthStore.getState().clear()
    }

    if (!resp.ok) {
        let code = 'ApiError'
        let message = `다운로드가 실패했어요 (${resp.status}).`
        try {
            const body = (await resp.json()) as {
                code?: string
                message?: string
            }
            code = body.code ?? code
            message = body.message ?? message
        } catch {
            // JSON이 아니면 기본 메시지를 유지한다.
        }
        throw new ApiError(code, message, resp.status)
    }

    const disposition = resp.headers.get('Content-Disposition') ?? ''
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
    const filename = match ? decodeURIComponent(match[1]) : fallbackName

    const blob = await resp.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

export const apiClient = {
    get: <T>(path: string) => request<T>(path, { method: 'GET' }),
    downloadBlob,
    post: <T>(path: string, body?: unknown) =>
        request<T>(path, {
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined,
        }),
    postForm: <T>(path: string, form: FormData) =>
        requestMultipart<T>(path, form),
    put: <T>(path: string, body?: unknown) =>
        request<T>(path, {
            method: 'PUT',
            body: body ? JSON.stringify(body) : undefined,
        }),
    patch: <T>(path: string, body?: unknown) =>
        request<T>(path, {
            method: 'PATCH',
            body: body ? JSON.stringify(body) : undefined,
        }),
    delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
